"use client";

import { useEffect, useState } from "react";
import { Mic } from "lucide-react";
import { cn } from "../../lib/utils";
import { acquireMicrophoneStream } from "../../utils/captureEnvironment";

const BAR_COUNT = 16;
const SILENCE_THRESHOLD = 0.02;
const WEAK_THRESHOLD = 0.08;
const GOOD_THRESHOLD = 0.35;
const LOUD_THRESHOLD = 0.7;
const CLIP_THRESHOLD = 0.95;

/**
 * Live mic equalizer + input quality check for the selected audioinput device.
 * @param {Object} props
 * @param {string} [props.deviceId]
 * @param {boolean} [props.active=true]
 * @param {string} [props.className]
 */
export default function MicInputMeter({ deviceId, active = true, className }) {
  const [bars, setBars] = useState(() => Array(BAR_COUNT).fill(0));
  const [level, setLevel] = useState(0);
  const [quality, setQuality] = useState("idle");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!active) {
      setBars(Array(BAR_COUNT).fill(0));
      setLevel(0);
      setQuality("idle");
      setError(null);
      return;
    }

    let cancelled = false;
    let stream = null;
    let audioContext = null;
    let analyser = null;
    let source = null;
    let rafId = null;
    let peakHold = 0;

    const stop = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      try {
        source?.disconnect();
      } catch {
        /* ignore */
      }
      source = null;
      analyser = null;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        stream = null;
      }
      if (audioContext && audioContext.state !== "closed") {
        void audioContext.close();
      }
      audioContext = null;
      peakHold = 0;
    };

    const start = async () => {
      setError(null);
      setQuality("starting");

      try {
        stream = await acquireMicrophoneStream(deviceId);

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          stream = null;
          return;
        }

        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        audioContext = new AudioContextClass();
        if (audioContext.state === "suspended") await audioContext.resume();

        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.65;
        analyser.minDecibels = -90;
        analyser.maxDecibels = -10;

        source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        const freqData = new Uint8Array(analyser.frequencyBinCount);
        const timeData = new Uint8Array(analyser.fftSize);

        const tick = () => {
          if (!analyser || cancelled) return;

          analyser.getByteFrequencyData(freqData);
          analyser.getByteTimeDomainData(timeData);

          const usable = freqData.slice(2);
          const nextBars = [];
          const chunk = Math.max(1, Math.floor(usable.length / BAR_COUNT));
          for (let i = 0; i < BAR_COUNT; i++) {
            let sum = 0;
            const startIdx = i * chunk;
            const endIdx = Math.min(startIdx + chunk, usable.length);
            for (let j = startIdx; j < endIdx; j++) sum += usable[j];
            const avg = sum / (endIdx - startIdx || 1);
            nextBars.push(Math.min(1, avg / 180));
          }
          setBars(nextBars);

          let sumSq = 0;
          let peak = 0;
          for (let i = 0; i < timeData.length; i++) {
            const v = (timeData[i] - 128) / 128;
            sumSq += v * v;
            const abs = Math.abs(v);
            if (abs > peak) peak = abs;
          }
          const rms = Math.sqrt(sumSq / timeData.length);
          const captureLevel = Math.min(1, rms * 3.2);
          setLevel(captureLevel);

          peakHold = Math.max(peakHold * 0.92, peak);

          if (peakHold >= CLIP_THRESHOLD) {
            setQuality("clipping");
          } else if (captureLevel < SILENCE_THRESHOLD) {
            setQuality("silent");
          } else if (captureLevel < WEAK_THRESHOLD) {
            setQuality("weak");
          } else if (captureLevel < GOOD_THRESHOLD) {
            setQuality("fair");
          } else if (captureLevel < LOUD_THRESHOLD) {
            setQuality("good");
          } else {
            setQuality("loud");
          }

          rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);
      } catch (err) {
        if (!cancelled) {
          console.error("Mic input meter failed:", err);
          setError(
            err?.name === "NotAllowedError"
              ? "Microphone permission denied"
              : "Could not open microphone"
          );
          setQuality("error");
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
      stop();
    };
  }, [active, deviceId]);

  const qualityMeta = {
    idle: {
      label: "Idle",
      color: "text-blue-400",
      bar: "bg-blue-200",
      glow: "",
    },
    starting: {
      label: "Starting…",
      color: "text-blue-600",
      bar: "bg-blue-400",
      glow: "",
    },
    silent: {
      label: "No signal — speak or check mic",
      color: "text-blue-500",
      bar: "bg-blue-300",
      glow: "",
    },
    weak: {
      label: "Weak — move closer or raise gain",
      color: "text-amber-700",
      bar: "bg-amber-400",
      glow: "",
    },
    fair: {
      label: "Fair — usable",
      color: "text-blue-600",
      bar: "bg-blue-500",
      glow: "shadow-sm shadow-blue-200",
    },
    good: {
      label: "Good — clear input",
      color: "text-teal-700",
      bar: "bg-gradient-to-t from-teal-600 to-teal-400",
      glow: "shadow-sm shadow-teal-300",
    },
    loud: {
      label: "Loud — reduce gain if distorted",
      color: "text-orange-700",
      bar: "bg-orange-500",
      glow: "",
    },
    clipping: {
      label: "Clipping — too hot, lower input",
      color: "text-red-600",
      bar: "bg-red-500",
      glow: "shadow-sm shadow-red-200",
    },
    error: {
      label: error || "Error",
      color: "text-red-600",
      bar: "bg-red-400",
      glow: "",
    },
  }[quality] || {
    label: "—",
    color: "text-blue-400",
    bar: "bg-blue-200",
    glow: "",
  };

  const levelPct = Math.round(level * 100);

  return (
    <div
      className={cn(
        "rounded-lg border border-blue-100 bg-blue-50/90 px-3 py-2.5 space-y-2 shadow-sm shadow-blue-100/60",
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-sm font-medium text-blue-700">
          <Mic className="h-3.5 w-3.5 text-blue-500" />
          Input level
        </div>
        <span className="text-sm font-semibold tabular-nums text-blue-700">
          {levelPct}%
        </span>
      </div>

      {/* Equalizer */}
      <div className="flex items-end justify-between gap-0.5 h-10 px-0.5">
        {bars.map((v, i) => {
          const hot = v > 0.85;
          const height = Math.max(3, Math.round(v * 40));
          return (
            <div
              key={i}
              className={cn(
                "flex-1 min-w-0 rounded-sm transition-[height] duration-75",
                hot ? "bg-red-500 shadow-sm shadow-red-200" : qualityMeta.bar,
                !hot && qualityMeta.glow
              )}
              style={{ height: `${height}px` }}
            />
          );
        })}
      </div>

      {/* Capture strength meter */}
      <div className="h-1.5 w-full rounded-full bg-blue-100 overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-75",
            quality === "good"
              ? "bg-gradient-to-r from-blue-500 to-teal-500"
              : qualityMeta.bar
          )}
          style={{ width: `${levelPct}%` }}
        />
      </div>

      <p className={cn("text-sm leading-snug font-medium", qualityMeta.color)}>
        {qualityMeta.label}
      </p>
    </div>
  );
}